package com.macrofocus.eflux.ui

import com.macrofocus.common.units.Kilovolt
import com.macrofocus.common.units.PhaseDegree
import com.macrofocus.eflux.EFluxConfiguration
import com.macrofocus.eflux.ViewLoader
import com.macrofocus.eflux.context.DisplayTooltip
import com.macrofocus.eflux.network.EFluxNetwork
import com.macrofocus.eflux.network.LinkField
import com.macrofocus.eflux.network.NodeField
import com.macrofocus.eflux.network.logical.LogicalNetwork
import mui.material.styles.Theme
import mui.material.styles.useTheme
import org.mkui.react.useProperty
import react.FC
import react.PropsWithChildren
import react.useRef
import web.cssom.ClassName
import web.html.HTMLDivElement
import web.svg.SVGElement
import kotlin.math.roundToInt

external interface DiagramProps : PropsWithChildren {
    var configuration: EFluxConfiguration
    var viewLoader: ViewLoader
    var show: Boolean
    var worldX: Double
    var worldY: Double
    var worldWidth: Double
    var worldHeight: Double
}

val Diagram = FC<DiagramProps> { props ->
    if (!props.show) {
        return@FC
    }

    val divRef = useRef<HTMLDivElement>(null)
    val svgRef = useRef<SVGElement>(null)

    val (dnd, _) = useProperty(props.configuration.dnd)
    val (tooltips, _) = useProperty(props.configuration.tooltips)

    ThemeUpdater {
        this.configuration = props.configuration
        this.viewLoader = props.viewLoader
    }

    DiagramUpdater {
        this.configuration = props.configuration
        this.viewLoader = props.viewLoader
    }

    +props.children

    TooltipContainer {
        this.efluxNetwork = props.viewLoader.efluxNetwork
        this.graphicalNetwork = props.viewLoader.graphicalNetwork
        this.tooltipNodes = props.configuration.tooltipNodes
        this.tooltipLinks = props.configuration.tooltipLinks
        this.background = "transparent"
        url = props.viewLoader.url
        this.divRef = divRef
        this.svgRef = svgRef
        svgsvg = props.viewLoader.svg
        svgPanZoomContext = props.viewLoader.svgPanZoomContext
        pointProbing = props.configuration.logicalBusProbing
        pathProbing = props.configuration.logicalBranchProbing
        pointSelection = props.configuration.logicalBusSelection
        pathSelection = props.configuration.logicalBranchSelection
        this.dnd = dnd
        this.tooltips = tooltips
        this.svgMatrix = props.viewLoader.svgMatrix
        this.activeTooltip = props.configuration.activeTooltips
        this.displayTooltip = if (props.configuration.tooltips.value) DisplayTooltip.Probing else DisplayTooltip.Never
        this.interactionMode = props.configuration.interactionMode
    }
}

external interface DiagramUpdaterProps : PropsWithChildren {
    var configuration: EFluxConfiguration
    var viewLoader: ViewLoader
}

val DiagramUpdater = FC<DiagramUpdaterProps> { props ->
    val theme = useTheme<Theme>()
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    val (efluxNetwork, _) = useProperty(props.viewLoader.efluxNetwork)

    val (colorNodes, _) = useProperty(props.configuration.colorNodes)
    val (sizeNodes, _) = useProperty(props.configuration.sizeNodes)

    val (thicknessLinks, _) = useProperty(props.configuration.thicknessLinks)
    val (colorLinks, _) = useProperty(props.configuration.colorLinks)
    val (movementLinks, _) = useProperty(props.configuration.movementLinks)
    val (animationLinks, _) = useProperty(props.configuration.animationLinks)

    if (efluxNetwork != null) {
        try {
            updateDiagram(
                efluxNetwork,
                efluxNetwork.logical,
                colorNodes,
                sizeNodes,
                thicknessLinks,
                colorLinks,
                movementLinks,
                animationLinks,
                paletteType
            )
        } catch (e: Exception) {
            console.log(e)
        }
    }
}

external interface ThemeUpdaterProps : PropsWithChildren {
    var configuration: EFluxConfiguration
    var viewLoader: ViewLoader
}

val ThemeUpdater = FC<ThemeUpdaterProps> { props ->
    val theme = useTheme<Theme>()
    val paletteType = if (theme.asDynamic().palette.mode == "dark") PaletteType.dark else PaletteType.light

    val (cities, _) = useProperty(props.configuration.cities)
    val (labelSubstations, _) = useProperty(props.configuration.labelSubstations)
    val (border, _) = useProperty(props.configuration.border)
    val (legend, _) = useProperty(props.configuration.legend)

    val (backgroundNodes, _) = useProperty(props.configuration.heatmapNodes)
    val (backgroundLinks, _) = useProperty(props.configuration.heatmapLinks)

    val (graphical, _) = useProperty(props.viewLoader.graphicalNetwork)

    val transparent = backgroundNodes != null || backgroundLinks != null
    val background: String = "background transparent"
    val country: String = if (transparent) "country transparent" else "country"

    val (viewStyle, _) = useProperty(props.configuration.viewStyle)

    graphical?.applyThemeColors(paletteType, ClassName(background), ClassName(country), cities, labelSubstations, border, legend, viewStyle)
}

fun nodeFill(paletteType: PaletteType) =
    if (paletteType == PaletteType.dark) "#0B374F" else "#FFFFFF"

fun nodeStroke(paletteType: PaletteType) =
    if (paletteType == PaletteType.dark) "#888888" else "#666666"

fun updateDiagram(
    efluxNetwork: EFluxNetwork,
    logicalNetwork: LogicalNetwork,
    colorField: NodeField<*>?,
    sizeNodes: NodeField<*>?,
    thicknessLinks: LinkField<*>?,
    colorLinks: LinkField<*>?,
    movementLinks: LinkField<*>?,
    animationLinks: Boolean,
    paletteType: PaletteType
) {
    val USE_MOVEMENT_CLASSES = true

    // Update default style
    for (node in efluxNetwork.nodes) {
        val point = node.point
        val bus = node.bus

        point.valid = bus != null
//        if (bus != null) {
//            point.available = logicalNetwork.getLinks(bus).all { it.branch.record.st == 1 }
//        }
        point.available = bus != null && bus.buses.any { it.record.ide != 4.0 }

        node.paletteType = paletteType

        point.noData = bus == null

//        node.inactive = bus == null || logicalNetwork.getLinks(bus).all { it.branch.record.st != 1 }

        // Test if it should remain in their original color
        val dreibein = node.id.name.startsWith("SY_") || node.id.name.startsWith("D4GU4_2")
        val colorField = if (dreibein && colorLinks == LinkField.baskv) NodeField.baskv else colorField

        if (dreibein && colorLinks == LinkField.baskv) {
            val className = if (bus != null) {
                val voltage = bus.buses.first().record.baskv
                when {
                    voltage >= 380.Kilovolt -> ClassName("kv380")
                    voltage >= 220.Kilovolt -> ClassName("kv220")
                    voltage >= 150.Kilovolt -> ClassName("kv150")
                    else -> null
                }
            } else {
                null
            }

            point.voltage = className
            node.fillColor = null
        } else {
            // Default colors from field from theme
            point.strokeColor = null
            node.fillColor = null
        }

        node.size = sizeNodes
        if (dreibein && colorLinks == LinkField.baskv) {
            node.color = null
        } else {
            node.color = colorField
        }
    }

    for (link in efluxNetwork.links) {
        val paths = link.path
        val branch = link.branch

        link.paletteType = paletteType

        paths.valid = branch != null
        paths.available = branch != null && branch.branch.record.st == 1

        if (branch != null) {
            val graphicalBranchId = paths.logicalBranchId

            val phaseAngle = branch.dva

            if (!link.inactive && movementLinks != null && phaseAngle != 0.0.PhaseDegree) {
                val normalized =
                    movementLinks.getMovement(link) // min(phaseAngle.amount.absoluteValue, phaseAngleMaximum) / phaseAngleMaximum
                if (normalized != null) {
                    // Inverted in graphical network?
                    var animationDirectionNormal = branch.logicalBranchId.isSameDirection(graphicalBranchId)

                    if (phaseAngle < 0.0.PhaseDegree) {
                        animationDirectionNormal = !animationDirectionNormal
                    }

                    if (!USE_MOVEMENT_CLASSES) {
                        val dashlength = ((normalized * (1 - 20)) + 20).roundToInt()
                        paths.strokeDasharray = "${dashlength},1"
                    }
                    if (animationLinks) {
                        if (USE_MOVEMENT_CLASSES) {
                            if(normalized <= 0.0) {
                                paths.movement = null
                                paths.movementReverse = false
                            } else {
                                val i = (normalized * 10).roundToInt()
                                paths.movement = ClassName("movement$i")
                                paths.movementReverse = !animationDirectionNormal
                            }
                        } else {
                            val minimum = 2500
                            val maximum = 500
                            val duration = ((normalized * (maximum - minimum)) + minimum).roundToInt()
                            paths.animationName = "dash"
                            paths.animationDuration = "${duration}s"
                            paths.animationIterationCount = "infinite"
                            paths.animationTimingFunction = "linear"
                            paths.animationDirection = if (!animationDirectionNormal) null else "reverse"
                        }
                    } else {
                        if (USE_MOVEMENT_CLASSES) {
                            paths.movementReverse = false
                            paths.movement = null
                        } else {
                            paths.animationName = null
                            paths.animationDuration = null
                            paths.animationIterationCount = null
                            paths.animationTimingFunction = null
                            paths.animationDirection = null
                        }
                    }
                } else {
                    if (USE_MOVEMENT_CLASSES) {
                        paths.movementReverse = false
                        paths.movement = null
                    } else {
                        paths.strokeDasharray = null
                        paths.animationName = null
                        paths.animationDuration = null
                        paths.animationIterationCount = null
                        paths.animationTimingFunction = null
                        paths.animationDirection = null
                        paths.animationDirection = null
                    }
                }
            } else {
                if (USE_MOVEMENT_CLASSES) {
                    paths.movementReverse = false
                    paths.movement = null
                } else {
                    paths.strokeDasharray = null
                    paths.animationName = null
                    paths.animationDuration = null
                    paths.animationIterationCount = null
                    paths.animationTimingFunction = null
                    paths.animationDirection = null
                    paths.animationDirection = null
                }
            }

            link.thickness = thicknessLinks
            link.color = colorLinks
        } else {
            link.thickness = null
            link.color = null

            if (USE_MOVEMENT_CLASSES) {
                paths.movementReverse = false
                paths.movement = null
            } else {
                paths.strokeDasharray = null
                paths.animationName = null
                paths.animationDuration = null
                paths.animationIterationCount = null
                paths.animationTimingFunction = null
                paths.animationDirection = null
                paths.animationDirection = null
            }
        }
    }
}

